import React, { useState, useEffect, Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LazyMotion, domAnimation } from "framer-motion";
import { BrowserRouter, Route, Routes, Outlet, useLocation } from "react-router-dom";

// Fixed imports: removed @ and used relative paths
import { Toaster as Sonner } from "./components/ui/sonner";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import Navbar from "./components/Navbar";

import Index from "./pages/Index";
// Code-split secondary routes and the auth modal (with its heavy phone-input
// dependency) so they stay out of the main bundle.
const BlogsPage = lazy(() => import("./pages/BlogsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AuthModal = lazy(() => import("./components/AuthModal"));

const queryClient = new QueryClient();

/**
 * ScrollToTop Component
 * Handles scrolling to the top on page change and 
 * scrolling to specific sections when a hash (#) is present.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // If there is no hash (e.g., navigating to /blogs), scroll to top
    if (!hash) {
      window.scrollTo(0, 0);
    } else {
      // If there is a hash (e.g., /#about), find the element and scroll to it
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [pathname, hash]);

  return null;
};

const AppLayout = ({ onOpenLogin, onOpenSignup, authMode, authOpen, onClose, onSwitch }) => (
  <>
    <ScrollToTop />
    <Navbar onOpenLogin={onOpenLogin} onOpenSignup={onOpenSignup} />
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
    {authOpen && (
      <Suspense fallback={null}>
        <AuthModal
          mode={authMode}
          open={authOpen}
          onClose={onClose}
          onSwitch={onSwitch}
        />
      </Suspense>
    )}
  </>
);

const App = () => {
  const [authMode, setAuthMode] = useState("login");
  const [authOpen, setAuthOpen] = useState(false);

  const openLogin = () => {
    setAuthMode("login");
    setAuthOpen(true);
  };

  const openSignup = () => {
    setAuthMode("signup");
    setAuthOpen(true);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <LazyMotion features={domAnimation} strict>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route 
              element={
                <AppLayout 
                  onOpenLogin={openLogin} 
                  onOpenSignup={openSignup} 
                  authMode={authMode} 
                  authOpen={authOpen} 
                  onClose={() => setAuthOpen(false)} 
                  onSwitch={() => setAuthMode(authMode === "login" ? "signup" : "login")} 
                />
              }
            >
              <Route path="/" element={<Index onOpenSignup={openSignup} />} />
              <Route path="/blogs" element={<BlogsPage />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
      </LazyMotion>
    </QueryClientProvider>
  );
};

export default App;